import { useContext, useState, useEffect } from 'react';
import { Search, Filter, Eye, Play, CheckCircle, Clock, AlertCircle, Award, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import AuthContext from '../../../../../context/AuthContext';
import { useToast } from '../../../../../context/ToastContext';
import Pagination from '../../../../../components/Pagination';
import myRecommendationService from '../../../../../services/audit_services/myRecommendationService';

export default function MyRecommendationsList() {
  const { authTokens, hasRole, hasAnyRole } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    ordering: 'created_at_desc'
  });

  
  const [appliedFilters, setAppliedFilters] = useState({ ...filters });
  const [showFilters, setShowFilters] = useState(false);
  
  const roleNames = ["Admin","AuditManager", "AuditUser", "RiskManager", "RiskIdentifier", "CartoViewer"]
  const canManage = hasAnyRole(roleNames);

  useEffect(() => {
    fetchMyRecommendations();
  }, [currentPage, appliedFilters]);

  const fetchMyRecommendations = async () => {
    setLoading(true);
    
    try {
      const result = await myRecommendationService.getMyRecommendations(
        authTokens.access,
        appliedFilters,
        {
          page: currentPage,
          page_size: itemsPerPage
        }
      );
      
      if (result.success) {
        setRecommendations(result.data.results || []);
        setTotalPages(Math.ceil(result.data.count / itemsPerPage));
        setTotalItems(result.data.count || 0);
      } else {
        showError(result.error || 'Error loading my recommendations');
        setRecommendations([]);
      }
    } catch (error) {
      console.error('Error fetching my recommendations:', error);
      showError('Error loading my recommendations');
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    const clearedFilters = {
      search: '',
      status: '',
      ordering: 'created_at_desc'
    };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    setCurrentPage(1);
  };

  const handleStartRecommendation = async (recommendationId) => {
    setActionLoading(prev => ({ ...prev, [recommendationId]: 'starting' }));
    
    try {
      const result = await myRecommendationService.startMyRecommendation(authTokens.access, recommendationId);
      
      if (result.success) {
        showSuccess('Recommendation started successfully');
        fetchMyRecommendations(); // Refresh the list
      } else {
        showError(result.error || 'Error starting recommendation');
      }
    } catch (error) {
      console.error('Error starting recommendation:', error);
      showError('Error starting recommendation');
    } finally {
      setActionLoading(prev => ({ ...prev, [recommendationId]: null }));
    }
  };

  const handleCompleteRecommendation = async (recommendationId) => {
    setActionLoading(prev => ({ ...prev, [recommendationId]: 'completing' }));
    
    try {
      const result = await myRecommendationService.completeMyRecommendation(authTokens.access, recommendationId);
      
      if (result.success) {
        showSuccess('Recommendation completed successfully');
        fetchMyRecommendations(); // Refresh the list
      } else {
        showError(result.error || 'Error completing recommendation');
      }
    } catch (error) {
      console.error('Error completing recommendation:', error);
      showError('Error completing recommendation');
    } finally {
      setActionLoading(prev => ({ ...prev, [recommendationId]: null }));
    }
  };

  const handleAchieveRecommendation = async (recommendationId) => {
    setActionLoading(prev => ({ ...prev, [recommendationId]: 'achieving' }));
    
    try {
      const result = await myRecommendationService.achieveMyRecommendation(authTokens.access, recommendationId);
      
      if (result.success) {
        showSuccess('Recommendation achieved successfully');
        fetchMyRecommendations(); // Refresh the list
      } else {
        showError(result.error || 'Error achieving recommendation');
      }
    } catch (error) {
      console.error('Error achieving recommendation:', error);
      showError('Error achieving recommendation');
    } finally {
      setActionLoading(prev => ({ ...prev, [recommendationId]: null }));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'En attente', icon: Clock },
      in_progress: { bg: 'bg-green-100', text: 'text-green-800', label: 'En cours', icon: Play },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé', icon: CheckCircle },
      achieved: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Réalisé', icon: Award },
      overdue: { bg: 'bg-red-100', text: 'text-red-800', label: 'En retard', icon: AlertCircle }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const getActionButtons = (recommendation) => {
    const buttons = [];
    const isLoading = actionLoading[recommendation.id];

    // View button (always available)
    buttons.push(
      <Link
        key="view"
        to={`/dashboard/audit/my-recommendations/${recommendation.id}`}
        className="p-1 text-gray-400 hover:text-green-600 transition-colors"
        title="Voir les détails"
      >
        <Eye className="h-4 w-4" />
      </Link>
    );

    if (canManage) {
      // Start button (only if pending)
      if (recommendation.status === 'pending') {
        buttons.push(
          <button
            key="start"
            onClick={() => handleStartRecommendation(recommendation.id)}
            disabled={isLoading}
            className="p-1 text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50"
            title="Démarrer la recommandation"
          >
            {isLoading === 'starting' ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent"></div>
            ) : (
              <Play className="h-4 w-4" />
            )}
          </button>
        );
      }

      // Close button (only if in progress)
      if (recommendation.status === 'in_progress') {
        buttons.push(
          <button
            key="close"
            onClick={() => handleCompleteRecommendation(recommendation.id)}
            disabled={isLoading}
            className="p-1 text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50"
            title="Clôturer la recommandation"
          >
            {isLoading === 'closing' ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent"></div>
            ) : (
              <CheckCircle className="h-4 w-4" />
            )}
          </button>
        );
      }

      // Achieve button (only if completed)
      if (recommendation.status === 'completed') {
        buttons.push(
          <button
            key="achieve"
            onClick={() => handleAchieveRecommendation(recommendation.id)}
            disabled={isLoading}
            className="p-1 text-gray-400 hover:text-yellow-600 transition-colors disabled:opacity-50"
            title="Archiver la recommandation"
          >
            {isLoading === 'achieving' ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-yellow-600 border-t-transparent"></div>
            ) : (
              <Award className="h-4 w-4" />
            )}
          </button>
        );
      }
    }

    return buttons;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mes recommandations</h1>
          <p className="text-sm text-gray-600 mt-1">
            Gérer les actions de recommandation qui vous sont assignées
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Target className="h-4 w-4" />
            <span>{totalItems} recommandations</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-900">Filtres</h3>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <Filter className="h-4 w-4 mr-1" />
            {showFilters ? 'Masquer' : 'Afficher'} les filtres
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Rechercher des recommandations..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="in_progress">En cours</option>
              <option value="completed">Terminé</option>
              <option value="achieved">Archivé</option>
              <option value="overdue">En retard</option>
            </select>

            {/* Ordering */}
            <select
              value={filters.ordering}
              onChange={(e) => handleFilterChange('ordering', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="created_at_desc">Plus récent d'abord</option>
              <option value="created_at_asc">Plus ancien d'abord</option>
              <option value="delay_asc">Délai (le plus court d'abord)</option>
              <option value="delay_desc">Délai (le plus long d'abord)</option>
              <option value="status_asc">Statut A-Z</option>
              <option value="status_desc">Statut Z-A</option>
            </select>

            {/* Filter Actions */}
            <div className="flex items-center space-x-2">
              <button
                onClick={applyFilters}
                className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
              >
                Appliquer les filtres
              </button>
              <button
                onClick={clearFilters}
                className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
              >
                Effacer
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="bg-white border border-gray-200 rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Chargement de mes recommandations...</p>
            </div>
          </div>
        ) : recommendations.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-gray-600 mb-4">Aucune recommandation ne vous est assignée</p>
            <p className="text-sm text-gray-500">
              Les recommandations apparaîtront ici lorsque vous serez désigné comme propriétaire
            </p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recommandation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date cible
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Question
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Créé
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recommendations.map((recommendation) => (
                    <tr key={recommendation.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="max-w-xs">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {recommendation.elements_of_recommendation}
                          </p>
                          {recommendation.kpi_of_recommendation && (
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              KPI: {recommendation.kpi_of_recommendation}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(recommendation.status)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">
                          {recommendation.delay_of_recommendation 
                            ? new Date(recommendation.delay_of_recommendation).toLocaleDateString()
                            : 'Non défini'
                          }
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-xs">
                          <Link
                            to={`/dashboard/audit/questions/${recommendation.question?.id}`}
                            className="text-sm text-green-600 hover:text-green-900 font-medium truncate block"
                          >
                            {recommendation.question?.question_name}
                          </Link>
                          <p className="text-xs text-gray-500 mt-1">
                            {recommendation.question?.audit_section?.section_name}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">
                          {new Date(recommendation.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end space-x-2">
                          {getActionButtons(recommendation)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t border-gray-200 px-6 py-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}